import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useLocation, useParams, useRouteError, type RouteObject } from 'react-router-dom'
import { AppLayout } from './layouts/AppLayout'
import { FEATURES } from '../shared/config/featureFlags'
import { PageLoader } from './PageLoader'
import { TableRowActionsProvider } from '../components/ui/Table'
import { RequireAccess } from './RequireAccess'
import { AgentAuthGate } from '../features/agent/AgentAuthGate'
import { LayoutGeneratorV2Scope } from '../features/layout-generator/layoutGeneratorNavigation'
const LayoutStudio = lazy(() => import('../features/layout-generator/LayoutStudioPage'))
const AssetLibrary = lazy(() => import('../features/asset-library/AssetLibraryPage'))

export { resolveRouteMetadata, routeMetadata, sectionPath } from './navigation'
export type { RouteMetadata } from './navigation'

// ── Retry wrapper for lazy imports (handles stale chunks after deploy) ──
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch((error: any) => {
      const isChunkError =
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk')

      if (isChunkError) {
        const reloadedKey = 'chunk_reload_' + window.location.pathname
        if (!sessionStorage.getItem(reloadedKey)) {
          sessionStorage.setItem(reloadedKey, '1')
          window.location.reload()
          return new Promise(() => {})
        }
        sessionStorage.removeItem(reloadedKey)
      }

      throw error
    }),
  )
}

// ── Route error fallback ────────────────────────────────────────────
function RouteErrorFallback() {
  const error = useRouteError() as Error | null
  const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module')

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm max-w-lg w-full p-8 text-center">
        <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {isChunkError ? 'Приложение обновилось' : 'Что-то пошло не так'}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {isChunkError
            ? 'Вышла новая версия приложения. Нажмите кнопку ниже для обновления.'
            : 'Произошла непредвиденная ошибка. Попробуйте обновить страницу.'}
        </p>
        {error && !isChunkError && (
          <details className="mb-6 text-left">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
              Техническая информация
            </summary>
            <pre className="mt-2 p-3 bg-gray-50 rounded text-xs text-red-700 overflow-auto max-h-32">
              {error.message}
            </pre>
          </details>
        )}
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center px-4 py-2 rounded-md font-medium text-sm bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          Обновить страницу
        </button>
      </div>
    </div>
  )
}

// ── Lazy-loaded pages ───────────────────────────────────────────────
const OAuthConsent = lazyWithRetry(() => import('../features/agent/OAuthConsentPage'))
const AgentAdmin = lazyWithRetry(() => import('../features/agent/AgentAdminPage'))
const AgentConnections = lazyWithRetry(() => import('../features/agent/AgentConnectionsPage'))
const BusinessAccess = lazyWithRetry(() => import('../features/business/BusinessAccessPage'))
const BusinessConnections = lazyWithRetry(() => import('../features/business/BusinessConnectionsPage'))
const BitrixLink = lazyWithRetry(() => import('../features/bitrix-bot/BitrixLinkPage'))
const BitrixBotAdmin = lazyWithRetry(() => import('../features/bitrix-bot/BitrixBotAdminPage'))
const Dashboard = lazyWithRetry(() => import('../pages/Dashboard'))
const Pipeline = lazyWithRetry(() => import('../features/pipeline/PipelinePage'))
const PipelineQuality = lazyWithRetry(() => import('../features/pipeline-quality/PipelineQualityPage'))
const IdeasPage = lazyWithRetry(() => import('../features/ideas/IdeasPage'))
const PipelineRules = lazyWithRetry(() => import('../features/pipeline-rules/PipelineRulesPage'))
const SeoModule = lazyWithRetry(() => import('../features/seo-v2/SeoModulePage'))
const SeoTemplateWizard = lazyWithRetry(
  () => import('../features/seo-v2-rules/wizard/TemplateWizardPage'),
)
const AttributesPage = lazyWithRetry(() => import('../features/attributes/AttributesPage'))
const BrandsPage = lazyWithRetry(() => import('../features/brands/BrandsPage'))
const CertificatesPage = lazyWithRetry(() => import('../features/certificates/CertificatesPage'))
const ContentFunnels = lazyWithRetry(() => import('../features/content-funnels/ContentFunnelsPage'))
const ContentFunnelStudio = lazyWithRetry(() => import('../features/content-funnel-studio/ContentFunnelStudioPage'))
const StudioDashboard = lazyWithRetry(() => import('../features/content-funnel-studio/v2/StudioDashboard'))
const StudioNewProject = lazyWithRetry(() => import('../features/content-funnel-studio/v2/StudioNewProject'))
const StudioProjectRoute = lazyWithRetry(() => import('../features/content-funnel-studio/v2/StudioProjectRoute'))
const SeriesFunnelNewPage = lazyWithRetry(() => import('../features/content-funnel-series/SeriesFunnelNewPage'))
const SeriesFunnelRunPage = lazyWithRetry(() => import('../features/content-funnel-series/SeriesFunnelRunPage'))
const SlideSetEditorPage = lazyWithRetry(() => import('../features/content-funnel-series/slide-sets/SlideSetEditorPage'))
const LegacyContentFunnels = lazyWithRetry(
  () => import('../features/content-funnels/LegacyContentFunnelsPage'),
)
const ProductsCatalog = lazyWithRetry(() => import('../features/products-v2/ProductsV2Page'))
const ProductsLegacy = lazyWithRetry(() => import('../features/products/ProductsPage'))
const GingerbreadCatalog = lazyWithRetry(
  () => import('../features/gingerbread-catalog/GingerbreadCatalogPage'),
)
// Генератор пряников v3 (04.10.2026): новая главная, мастер создания и студия серии (по образцу генератора макетов).
const GingerbreadJobs = lazyWithRetry(
  () => import('../features/gingerbread-v3/GingerbreadHomePage'),
)
const GingerbreadCreate = lazyWithRetry(
  () => import('../features/gingerbread-v3/create/GingerbreadCreatePage'),
)
const GingerbreadStudio = lazyWithRetry(
  () => import('../features/gingerbread-v3/studio/GingerbreadStudioPage'),
)
const GingerbreadJob = lazyWithRetry(
  () => import('../features/gingerbread-v3/passport/GingerbreadPassportPage'),
)
const GingerbreadShapes = lazyWithRetry(
  () => import('../features/gingerbread-generator/GingerbreadShapesPage'),
)
const GingerbreadLegacy = lazyWithRetry(
  () => import('../features/gingerbread-legacy/GingerbreadLegacyPage'),
)
const LayoutJobs = lazyWithRetry(
  () => import('../features/layout-generator').then((module) => ({ default: module.LayoutJobsPage })),
)
const LayoutJobsV2 = lazyWithRetry(() => import('../features/layout-generator/v2/LayoutJobsV2Page'))
const LayoutJobCreate = lazyWithRetry(
  () => import('../features/layout-generator').then((module) => ({ default: module.LayoutJobCreatePage })),
)
const LayoutJob = lazyWithRetry(
  () => import('../features/layout-generator').then((module) => ({ default: module.LayoutJobPage })),
)
const LayoutDuplication = lazyWithRetry(
  () => import('../features/layout-generator').then((module) => ({ default: module.LayoutDuplicationPage })),
)
const LayoutGeneratorHelp = lazyWithRetry(() => import('../features/layout-generator/help/LayoutGeneratorHelpPage'))
const DesignsCatalog = lazyWithRetry(() => import('../features/designs-v2/DesignsV2Page'))
const DesignsLegacy = lazyWithRetry(() => import('../pages/Designs'))
const Categories = lazyWithRetry(() => import('../pages/Categories'))
const DesignTypeMatrixEditor = lazyWithRetry(() => import('../components/DesignTypeMatrixEditor'))
const MediaMappings = lazyWithRetry(() => import('../pages/MediaMappings'))
const MarketplaceMapping = lazyWithRetry(
  () => import('../features/marketplace-mapping/MarketplaceMappingPage'),
)
const UploadTemplates = lazyWithRetry(() => import('../pages/UploadTemplates'))
const ArticleMappings = lazyWithRetry(() => import('../pages/ArticleMappings'))
const Sellers = lazyWithRetry(() => import('../pages/Sellers'))
const UsersAccess = lazyWithRetry(() => import('../features/users-access/UsersAccessPage'))
const WildberriesCatalog = lazyWithRetry(
  () => import('../features/wildberries/catalog/WildberriesCatalogPage'),
)
const WildberriesLegacy = lazyWithRetry(() => import('../pages/Wildberries'))
const WbCardsEditor = lazyWithRetry(() => import('../features/wb-cards-editor/WbCardsEditorPage'))
const ProductLinking = lazyWithRetry(() => import('../pages/ProductLinking'))
const OzonCatalog = lazyWithRetry(() => import('../features/ozon/catalog/OzonCatalogPage'))
const OzonLegacy = lazyWithRetry(() => import('../pages/Ozon'))
const YandexMarketCatalog = lazyWithRetry(
  () => import('../features/yandex-market/catalog/YandexMarketCatalogPage'),
)
const MarketplacePublishPreview = lazyWithRetry(
  () => import('../features/marketplace-publish-preview/PublishPreviewPage'),
)
const MarketplaceCardChanges = lazyWithRetry(
  () => import('../features/marketplace-card-changes/MarketplaceCardChangesPage'),
)
const OneCProducts = lazyWithRetry(() => import('../features/onec/OneCProductsPage'))
const SiteProducts = lazyWithRetry(() => import('../features/site/SiteProductsPage'))
const MoySklad = lazyWithRetry(() => import('../pages/MoySklad'))
const Orders = lazyWithRetry(() => import('../pages/Orders'))
const Notifications = lazyWithRetry(() => import('../features/notifications/NotificationsPage'))
const NotificationPreferences = lazyWithRetry(
  () => import('../features/notifications/NotificationPreferencesPage'),
)
const AuditLog = lazyWithRetry(() => import('../pages/AuditLog'))
const Search = lazyWithRetry(() => import('../pages/Search'))
const Settings = lazyWithRetry(() => import('../pages/Settings').then((m) => ({ default: m.Settings })))
const AdminConsole = lazyWithRetry(
  () => import('../pages/AdminConsole').then((m) => ({ default: m.AdminConsole })),
)
const Logs = lazyWithRetry(() => import('../pages/Logs'))
const LoginPage = lazyWithRetry(() => import('../pages/LoginPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('../pages/ForgotPasswordPage'))
const SetPasswordPage = lazyWithRetry(() => import('../pages/SetPasswordPage'))
const DesignSystem = lazyWithRetry(() => import('../pages/DesignSystem'))
const StubPage = lazyWithRetry(() => import('../pages/StubPage'))
const StorageSettings = lazyWithRetry(() => import('../features/storage-settings/StorageSettingsPage'))
const ProdSync = lazyWithRetry(() => import('../features/prod-sync/ProdSyncPage'))
const AiSettings = lazyWithRetry(() => import('../features/ai-settings/AiSettingsPage'))
const Integrations = lazyWithRetry(() => import('../features/integrations/IntegrationsPage'))
const MailSettings = lazyWithRetry(() => import('../pages/MailSettings'))
const Backlog = lazyWithRetry(() => import('../features/work-items/BacklogPage'))
const MyReports = lazyWithRetry(() => import('../features/work-items/MyReportsPage'))
// ── ЛК Дизайнера ──
const DesignerCreateLayouts = lazyWithRetry(() => import('../features/designer/CreateLayoutsPage'))
const DesignerStats = lazyWithRetry(() => import('../features/designer/StatsPage'))
const DesignerPayouts = lazyWithRetry(() => import('../features/designer/PayoutsPage'))
const DesignerTariffs = lazyWithRetry(() => import('../features/designer/TariffsPage'))
const PriceIndex = lazyWithRetry(() => import('../features/price-index/PriceIndexPage'))
const PricingWb = lazyWithRetry(() => import('../features/pricing/PricingPage'))
const StockStopList = lazyWithRetry(() => import('../features/stocks/StopListPage'))
const StockAutoupdate = lazyWithRetry(() => import('../features/stocks/AutoupdatePage'))
const StockRuns = lazyWithRetry(() => import('../features/stocks/RunsPage'))
const StockWarehouses = lazyWithRetry(() => import('../features/stocks/WarehousesPage'))
const StockSettings = lazyWithRetry(() => import('../features/stocks/StockSettingsPage'))
const StockBannedBrands = lazyWithRetry(() => import('../features/stocks/BannedBrandsPage'))
const StockUnliquid = lazyWithRetry(() => import('../features/stocks/UnliquidPage'))
const StockManual = lazyWithRetry(() => import('../features/stocks/ManualStocksPage'))
const AnalyticsHeroes = lazyWithRetry(() => import('../features/analytics/heroes/HeroesPage'))
const AnalyticsCost = lazyWithRetry(() => import('../features/cost/CostPage'))
const AnalyticsSales = lazyWithRetry(() => import('../features/analytics/sales/SalesPage'))
const AnalyticsDataQuality = lazyWithRetry(() => import('../features/analytics/data-quality/DataQualityPage'))
const AnalyticsMetricsRegistry = lazyWithRetry(() => import('../features/metrics-registry/MetricsRegistryPage'))
const AnalyticsMetricsExplorer = lazyWithRetry(() => import('../features/metrics-explorer/MetricsExplorerPage'))
const AnalyticsSeasonal = lazyWithRetry(() => import('../features/analytics/seasonal/SeasonalPage'))
const AnalyticsDesigns = lazyWithRetry(() => import('../features/analytics/assortment/DesignsPage'))
const AnalyticsAbc = lazyWithRetry(() => import('../features/analytics/assortment/AbcPage'))
const AnalyticsCabinets = lazyWithRetry(() => import('../features/analytics/assortment/CabinetsPage'))
const AnalyticsStocks = lazyWithRetry(() => import('../features/analytics/stocks/StocksPage'))
// Волна 5 «Деньги»: P&L, расходы площадок, сверка; юнит-экономика и ценообразование Ozon (были заглушками).
const AnalyticsPnl = lazyWithRetry(() => import('../features/analytics/finance/PnlPage'))
const AnalyticsExpenses = lazyWithRetry(() => import('../features/analytics/finance/ExpensesPage'))
const AnalyticsReconciliation = lazyWithRetry(() => import('../features/analytics/finance/ReconciliationPage'))
// Волна 6 «Управление»: сводка, алерты, РНП, план-факт; воронка, реклама, цены и акции, отзывы, география.
const AnalyticsSummary = lazyWithRetry(() => import('../features/analytics/summary/SummaryPage'))
const AnalyticsAlerts = lazyWithRetry(() => import('../features/analytics/alerts/AlertsPage'))
const AnalyticsRnp = lazyWithRetry(() => import('../features/analytics/rnp/RnpPage'))
const AnalyticsPlan = lazyWithRetry(() => import('../features/analytics/plan/PlanPage'))
const AnalyticsFunnel = lazyWithRetry(() => import('../features/analytics/funnel/FunnelPage'))
const AnalyticsAds = lazyWithRetry(() => import('../features/analytics/ads/AdsPage'))
const AnalyticsPrices = lazyWithRetry(() => import('../features/analytics/prices/PricesPage'))
const AnalyticsFeedbacks = lazyWithRetry(() => import('../features/analytics/feedbacks/FeedbacksPage'))
const AnalyticsGeo = lazyWithRetry(() => import('../features/analytics/geo/GeoPage'))
const AnalyticsReturns = lazyWithRetry(() => import('../features/analytics/returns/ReturnsPage'))
const UnitEconomics = lazyWithRetry(() => import('../features/pricing/unit-economics/UnitEconomicsPage'))
const PricingOzon = lazyWithRetry(() => import('../features/pricing/ozon/OzonPricingPage'))
const SuppliesPage = lazyWithRetry(() => import('../features/supplies/SuppliesPage'))
const DesignersTabs = lazyWithRetry(() => import('../features/designers/DesignersTabs'))

// ── Loading fallback ────────────────────────────────────────────────
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

function LayoutGeneratorV2Page({ children }: { children: React.ReactNode }) {
  return <LazyPage><LayoutGeneratorV2Scope>{children}</LayoutGeneratorV2Scope></LazyPage>
}

// ── Адреса модуля SEO ───────────────────────────────────────────────
/*
 * Модуль SEO переехал на две страницы шаблона v2 (ADR 001). Канонические
 * адреса — старые: `/products-seo` и `/products-seo/rules`. Всё остальное,
 * включая временные адреса `-v2`, отвечает редиректом: за время редизайна
 * разошлись ссылки обоих поколений, и ни одна не имеет права умереть.
 *
 * Правило переноса: `location.search` уходит целиком, параметры пути
 * переезжают в query там, где у новой страницы для них есть параметр.
 */

/** Старые имена параметров → имена новой страницы «Правила SEO». */
const RULES_PARAM_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ['marketplace', 'mp'],
  ['category_prefix', 'cat'],
  ['seller_id', 'sid'],
  ['seller', 'sid'],
]

interface SeoSettingsDestination {
  tab: string
  config: string | null
}

/** Старые вкладки настроек ведут либо в основную ленту, либо в техпанель. */
const SETTINGS_TAB_DESTINATIONS: Readonly<Record<string, SeoSettingsDestination>> = {
  fragments: { tab: 'blocks', config: null },
  keys: { tab: 'rules', config: 'mappings' },
  generators: { tab: 'rules', config: 'assembly' },
  rulesets: { tab: 'rules', config: 'checks' },
  overview: { tab: 'rules', config: null },
}

export function settingsTabDestination(tab: string | null | undefined): SeoSettingsDestination {
  return (tab && SETTINGS_TAB_DESTINATIONS[tab]) || { tab: 'rules', config: 'checks' }
}

/**
 * Перенос строки запроса. Без патча строка уходит байт в байт: пересылать
 * ссылку и получать её же перекодированной незачем. С патчем параметры
 * дописываются поверх, а старые имена дублируются новыми — исходные при этом
 * остаются, чтобы ссылка не потеряла ничего из того, что в ней было.
 */
export function carrySearch(
  search: string,
  patch?: Record<string, string | null>,
  aliases: ReadonlyArray<readonly [string, string]> = [],
): string {
  if (!patch && !aliases.length) return search
  const params = new URLSearchParams(search)
  for (const [from, to] of aliases) {
    const value = params.get(from)
    if (value !== null && !params.has(to)) params.set(to, value)
  }
  for (const [key, value] of Object.entries(patch || {})) {
    if (value === null) params.delete(key)
    else params.set(key, value)
  }
  const result = params.toString()
  return result ? `?${result}` : ''
}

/** Строка запроса для редиректа на страницу «Правила SEO». */
export function rulesSearch(search: string, patch: Record<string, string | null>): string {
  return carrySearch(search, patch, RULES_PARAM_ALIASES)
}

const SEO_MODULE_PATH = '/products-seo'
const RULES_PATH = '/products-seo/rules'

/** Редирект «как есть»: меняется только путь. */
function pathRedirect(pathname: string) {
  return function PathRedirect() {
    const location = useLocation()
    return <Navigate to={{ pathname, search: location.search }} replace />
  }
}

/** Редирект на вкладку объединённой страницы с сохранением остальных query. */
export function tabRedirect(pathname: string, tab: string) {
  return function TabRedirect() {
    const location = useLocation()
    return <Navigate to={`${pathname}${carrySearch(location.search, { tab })}`} replace />
  }
}

/** Бывшая страница «Воронки серий» → вкладка «По тематикам» / «По товарам» страницы «Контент-воронки». */
export function SeriesFunnelsRedirect() {
  const location = useLocation()
  const tab = new URLSearchParams(location.search).get('tab') === 'sku' ? 'series-sku' : 'series-theme'
  return <Navigate to={`/content-funnels?tab=${tab}`} replace />
}

/** Редирект старого адреса «Правила SEO» на вкладку общего SEO-модуля. */
export function SeoRulesAlias() {
  const location = useLocation()
  const search = new URLSearchParams(location.search).has('tab')
    ? location.search
    : carrySearch(location.search, { tab: 'rules' })
  return <Navigate to={`${SEO_MODULE_PATH}${search}`} replace />
}

/** Редирект на внутреннюю вкладку страницы «Правила SEO». */
function rulesTabRedirect(tab: string, extra?: Record<string, string | null>) {
  return function RulesTabRedirect() {
    const location = useLocation()
    return <Navigate to={`${SEO_MODULE_PATH}${rulesSearch(location.search, { tab, ...extra })}`} replace />
  }
}

export const SeoCardsAlias = pathRedirect(SEO_MODULE_PATH)
export const SeoV2RulesAlias = SeoRulesAlias
export const SeoV2TemplateNewAlias = pathRedirect(`${RULES_PATH}/template/new`)
export const SeoTemplatesAlias = rulesTabRedirect('templates')
export const SeoLibraryAlias = rulesTabRedirect('blocks')
export const SeoSemanticsAlias = rulesTabRedirect('keywords')
export const SeoCampaignsAlias = rulesTabRedirect('campaigns')
export const SeoCampaignNewAlias = rulesTabRedirect('campaigns', { campaign: 'new' })

export const DesignerPayoutsAlias = tabRedirect('/designers', 'payouts')
export const AccessManagementAlias = tabRedirect('/users', 'access')
export const ExternalServicesAlias = tabRedirect('/integrations', 'services')
export const MarketplaceLinkAlias = tabRedirect('/integrations', 'link')
export const OneCIntegrationAlias = tabRedirect('/integrations', '1c')
export const SiteIntegrationAlias = tabRedirect('/integrations', 'site')
export const LicensiumIntegrationAlias = tabRedirect('/integrations', 'licensium')
export const FtpSettingsAlias = tabRedirect('/settings/storage', 'ftp')
export const MaketsStorageAlias = tabRedirect('/settings/storage', 'makets')

/*
 * Каталоги нового поколения заняли короткие адреса: `/designs`, `/products`,
 * `/wildberries`, `/ozon`. Предыдущее поколение уехало под суффикс `-old`
 * (пункты меню — в группе «Архив»), а временные адреса `-v2` остались
 * редиректами: за время редизайна их успели разослать ссылками на карточки
 * (`/designs-v2?design=...`), и строка запроса уходит на новый адрес целиком.
 */
export const DesignsV2Alias = pathRedirect('/designs')
export const ProductsV2Alias = pathRedirect('/products')
export const WildberriesV2Alias = pathRedirect('/wildberries')
export const OzonV2Alias = pathRedirect('/ozon')
export const SiteProductsAlias = pathRedirect('/site-cards')

/*
 * Заглушки, которые дублировали страницы аналитики (решение архитектора, волна 4): старые адреса ведут на
 * настоящие страницы, строка запроса уходит целиком.
 */
export const DesignsAnalyticsAlias = pathRedirect('/analytics/designs')
export const SeasonalProductsAlias = pathRedirect('/analytics/seasonal')
export const SeasonalEventsAlias = pathRedirect('/analytics/seasonal')

/**
 * Карточка перестала быть страницей и стала окном над списком: три параметра
 * пути переезжают в query, где их ждёт список.
 */
export function SeoCardAlias() {
  const { marketplace, sellerId, article } = useParams()
  const location = useLocation()
  const search = carrySearch(location.search, {
    mp: marketplace === 'ozon' ? 'ozon' : 'wb',
    seller: sellerId || null,
    article: article || null,
  })
  return <Navigate to={`${SEO_MODULE_PATH}${search}`} replace />
}

/** Мастер массовых изменений — окно списка, адресуемое `bulk=1`. */
export function SeoGenerateAlias() {
  const location = useLocation()
  return <Navigate to={`${SEO_MODULE_PATH}${carrySearch(location.search, { bulk: '1' })}`} replace />
}

/** Публикация перестала быть разделом и стала видом «К публикации». */
export function SeoPublicationsAlias() {
  const location = useLocation()
  return <Navigate to={`${SEO_MODULE_PATH}${carrySearch(location.search, { state: 'ready' })}`} replace />
}

/** Эффективность перестала быть разделом и стала окном показателей. */
export function SeoAnalyticsAlias() {
  const location = useLocation()
  return <Navigate to={`${SEO_MODULE_PATH}${carrySearch(location.search, { metrics: '1' })}`} replace />
}

/** `/products-seo/settings` и его двойник `/products-seo/quality`. */
export function SeoSettingsAlias() {
  const location = useLocation()
  const destination = settingsTabDestination(new URLSearchParams(location.search).get('tab'))
  return (
    <Navigate
      to={`${SEO_MODULE_PATH}${rulesSearch(location.search, {
        tab: destination.tab,
        config: destination.config,
      })}`}
      replace
    />
  )
}

/** Редактор шаблона: старый адрес правки и временный адрес `-v2`. */
export function SeoTemplateEditAlias() {
  const { id } = useParams()
  const location = useLocation()
  return <Navigate to={`${RULES_PATH}/template/${id}${location.search}`} replace />
}

/** Редактор кампании: идентификатор переезжает в query окна. */
export function SeoCampaignAlias() {
  const { id } = useParams()
  const location = useLocation()
  return (
    <Navigate
      to={`${SEO_MODULE_PATH}${rulesSearch(location.search, { tab: 'campaigns', campaign: id || null })}`}
      replace
    />
  )
}

/**
 * Таблица адресов модуля SEO отдельным массивом: по ней же идёт проверка
 * переходов в `router.seo-aliases.test.tsx`. Порядок значим — специфичные
 * пути стоят до `:id`-шаблонов.
 */
export const seoRoutes: { path: string; element: React.ReactNode }[] = [
  // Канонический контейнер и совместимый адрес прежней страницы правил.
  { path: 'products-seo', element: <LazyPage><SeoModule /></LazyPage> },
  { path: 'products-seo/rules', element: <SeoRulesAlias /> },
  // `template/new` — до `template/:id`: иначе «new» попадёт в :id и мастер
  // попытается открыть шаблон с идентификатором NaN.
  { path: 'products-seo/rules/template/new', element: <LazyPage><SeoTemplateWizard /></LazyPage> },
  { path: 'products-seo/rules/template/:id', element: <LazyPage><SeoTemplateWizard /></LazyPage> },
  // Разосланные адреса старого модуля
  { path: 'products-seo/cards', element: <SeoCardsAlias /> },
  { path: 'products-seo/generate', element: <SeoGenerateAlias /> },
  { path: 'products-seo/publications', element: <SeoPublicationsAlias /> },
  { path: 'products-seo/analytics', element: <SeoAnalyticsAlias /> },
  { path: 'products-seo/templates', element: <SeoTemplatesAlias /> },
  { path: 'products-seo/templates/new', element: <SeoV2TemplateNewAlias /> },
  { path: 'products-seo/templates/:id/edit', element: <SeoTemplateEditAlias /> },
  { path: 'products-seo/library', element: <SeoLibraryAlias /> },
  { path: 'products-seo/semantics', element: <SeoSemanticsAlias /> },
  { path: 'products-seo/campaigns', element: <SeoCampaignsAlias /> },
  { path: 'products-seo/campaigns/new', element: <SeoCampaignNewAlias /> },
  { path: 'products-seo/campaigns/:id', element: <SeoCampaignAlias /> },
  { path: 'products-seo/settings', element: <SeoSettingsAlias /> },
  { path: 'products-seo/quality', element: <SeoSettingsAlias /> },
  { path: 'products-seo/card/:marketplace/:sellerId/:article', element: <SeoCardAlias /> },
  { path: 'products-seo/:marketplace/:sellerId/:article', element: <SeoCardAlias /> },
  // Временные адреса редизайна: их тоже успели разослать
  { path: 'products-seo-v2', element: <SeoCardsAlias /> },
  { path: 'products-seo-v2/rules', element: <SeoV2RulesAlias /> },
  { path: 'products-seo-v2/rules/template/new', element: <SeoV2TemplateNewAlias /> },
  { path: 'products-seo-v2/rules/template/:id', element: <SeoTemplateEditAlias /> },
]

// ── Route definitions ───────────────────────────────────────────────
/** Apply the same permission boundary to every protected child, including redirects. */
export function guardRoutes(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    if (route.path === '*') return route
    return {
      ...route,
      element: <RequireAccess>{route.element}</RequireAccess>,
      children: route.children ? guardRoutes(route.children) : route.children,
    } as RouteObject
  })
}

export const router = createBrowserRouter([
  {
    // Ссылка из личного сообщения бота «Аналитик PrinTort»: вне основной оболочки, вход с возвратом на ссылку.
    path: '/integrations/bitrix/link',
    errorElement: <RouteErrorFallback />,
    element: <AgentAuthGate><LazyPage><BitrixLink /></LazyPage></AgentAuthGate>,
  },
  {
    path: '/oauth/consent',
    errorElement: <RouteErrorFallback />,
    element: <div className="min-h-screen bg-zinc-50 p-6 dark:bg-zinc-950"><AgentAuthGate><LazyPage><OAuthConsent /></LazyPage></AgentAuthGate></div>,
  },
  {
    path: '/login',
    errorElement: <RouteErrorFallback />,
    element: (
      <LazyPage>
        <LoginPage />
      </LazyPage>
    ),
  },
  {
    path: '/forgot-password',
    errorElement: <RouteErrorFallback />,
    element: (
      <LazyPage>
        <ForgotPasswordPage />
      </LazyPage>
    ),
  },
  {
    path: '/reset-password',
    errorElement: <RouteErrorFallback />,
    element: (
      <LazyPage>
        <SetPasswordPage mode="reset" />
      </LazyPage>
    ),
  },
  {
    path: '/activate',
    errorElement: <RouteErrorFallback />,
    element: (
      <LazyPage>
        <SetPasswordPage mode="activate" />
      </LazyPage>
    ),
  },
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorFallback />,
    children: guardRoutes([
      { index: true, element: <LazyPage><Dashboard /></LazyPage> },
      { path: 'themes-board', element: <Navigate to="/ideas" replace /> },
      { path: 'ideas', element: <LazyPage><IdeasPage /></LazyPage> },
      { path: 'pipeline', element: <LazyPage><Pipeline /></LazyPage> },
      { path: 'pipeline/source-quality', element: <LazyPage><PipelineQuality /></LazyPage> },
      { path: 'pipeline-rules', element: <LazyPage><PipelineRules /></LazyPage> },
      { path: 'attributes', element: <LazyPage><AttributesPage /></LazyPage> },
      { path: 'brands', element: <LazyPage><BrandsPage /></LazyPage> },
      { path: 'content-funnels', element: <LazyPage><ContentFunnels /></LazyPage> },
      {
        path: 'content-funnel-studio',
        element: (
          <LazyPage>
            {FEATURES.contentFunnelStudioV2 ? <StudioDashboard /> : <ContentFunnelStudio />}
          </LazyPage>
        ),
      },
      // Резерв для FEATURES.contentFunnelStudioV2: ссылка «Открыть прежнюю форму»
      // из StudioNewProject, когда сервер временно не готов создавать v2-проекты
      // (`capabilities.featureEnabled===false`) — сам путь /content-funnel-studio
      // при включённом флаге занят StudioDashboard, старой форме нужен свой адрес.
      { path: 'content-funnel-studio-legacy', element: <LazyPage><ContentFunnelStudio /></LazyPage> },
      { path: 'content-funnel-studio/new', element: <LazyPage><StudioNewProject /></LazyPage> },
      {
        path: 'content-funnel-studio/:id',
        element: (
          <LazyPage>
            {FEATURES.contentFunnelStudioV2 ? <StudioProjectRoute /> : <ContentFunnelStudio />}
          </LazyPage>
        ),
      },
      // Воронка в стиле серии: список — вкладки «Контент-воронок», здесь мастер и страница запуска (специфичный `new` — до `:runId`).
      { path: 'content-funnel-series', element: <SeriesFunnelsRedirect /> },
      { path: 'content-funnel-series/new', element: <LazyPage><SeriesFunnelNewPage /></LazyPage> },
      // Редактор наборов слайдов: специфичные пути — до `:runId`, иначе `slide-sets` разберётся как запуск.
      { path: 'content-funnel-series/slide-sets/new', element: <LazyPage><SlideSetEditorPage /></LazyPage> },
      { path: 'content-funnel-series/slide-sets/:setId', element: <LazyPage><SlideSetEditorPage /></LazyPage> },
      { path: 'content-funnel-series/:runId', element: <LazyPage><SeriesFunnelRunPage /></LazyPage> },
      // Старый путь «Шаблоны генерации» → редирект на объединённую страницу «Контент воронки»
      { path: 'generation-templates', element: <Navigate to="/content-funnels" replace /> },
      { path: 'search', element: <LazyPage><Search /></LazyPage> },
      { path: 'products', element: <LazyPage><ProductsCatalog /></LazyPage> },
      { path: 'products-old', element: <LazyPage><ProductsLegacy /></LazyPage> },
      { path: 'products-v2', element: <ProductsV2Alias /> },
      { path: 'gingerbreads', element: <LazyPage><GingerbreadCatalog /></LazyPage> },
      // Генератор пряников: задания (v3 — главная, мастер, студия; v2 — страница задания). Старые страницы наборов (/new, /:setId, /import/:templateId) удалены на этапе 7 вместе с кодом.
      { path: 'gingerbread-generator', element: <LazyPage><GingerbreadJobs /></LazyPage> },
      { path: 'gingerbread-generator/new', element: <LazyPage><GingerbreadCreate /></LazyPage> },
      { path: 'gingerbread-generator/studio/:jobId', element: <LazyPage><GingerbreadStudio /></LazyPage> },
      { path: 'gingerbread-generator/jobs/:jobId', element: <LazyPage><GingerbreadJob /></LazyPage> },
      { path: 'gingerbread-generator/shapes', element: <LazyPage><GingerbreadShapes /></LazyPage> },
      // Импорт старых наборов PP100001–PP100173 (этап 7); вход — кнопка «Импорт» в каталоге пряников.
      { path: 'gingerbread-generator/legacy', element: <LazyPage><GingerbreadLegacy /></LazyPage> },
      ...(FEATURES.layoutGenerator ? [
        { path: 'layout-generator', element: <LazyPage><LayoutJobs /></LazyPage> },
        { path: 'layout-generator/studio/:jobId', element: <LazyPage><LayoutStudio /></LazyPage> },
        { path: 'layout-generator/duplicate', element: <LazyPage><LayoutDuplication /></LazyPage> },
        { path: 'layout-generator/duplicate/:jobId', element: <LazyPage><LayoutDuplication /></LazyPage> },
        { path: 'asset-library', element: <LazyPage><AssetLibrary /></LazyPage> },
        { path: 'layout-generator/new', element: <LazyPage><LayoutJobCreate /></LazyPage> },
        { path: 'layout-generator/help', element: <LazyPage><LayoutGeneratorHelp /></LazyPage> },
        { path: 'layout-generator/jobs/:jobId', element: <LazyPage><LayoutJob /></LazyPage> },
        { path: 'layout-generator-v2', element: <LayoutGeneratorV2Page><LayoutJobsV2 /></LayoutGeneratorV2Page> },
        { path: 'layout-generator-v2/studio/:jobId', element: <LayoutGeneratorV2Page><LayoutStudio /></LayoutGeneratorV2Page> },
        { path: 'layout-generator-v2/duplicate', element: <LayoutGeneratorV2Page><LayoutDuplication /></LayoutGeneratorV2Page> },
        { path: 'layout-generator-v2/duplicate/:jobId', element: <LayoutGeneratorV2Page><LayoutDuplication /></LayoutGeneratorV2Page> },
        { path: 'layout-generator-v2/new', element: <LayoutGeneratorV2Page><LayoutJobCreate /></LayoutGeneratorV2Page> },
        { path: 'layout-generator-v2/jobs/:jobId', element: <LayoutGeneratorV2Page><LayoutJob /></LayoutGeneratorV2Page> },
      ] : []),
      // BulkEditProducts is embedded within the legacy Products page (requires props)
      { path: 'designs', element: <LazyPage><DesignsCatalog /></LazyPage> },
      { path: 'designs-old', element: <LazyPage><DesignsLegacy /></LazyPage> },
      { path: 'designs-v2', element: <DesignsV2Alias /> },
      { path: 'categories', element: <LazyPage><Categories /></LazyPage> },
      { path: 'certificates', element: <LazyPage><CertificatesPage /></LazyPage> },
      { path: 'marketplace-mapping', element: <LazyPage><MarketplaceMapping /></LazyPage> },
      { path: 'design-type-matrix', element: <LazyPage><DesignTypeMatrixEditor /></LazyPage> },
      { path: 'media-mappings', element: <LazyPage><MediaMappings /></LazyPage> },
      { path: 'upload-templates', element: <LazyPage><UploadTemplates /></LazyPage> },
      { path: 'article-mappings', element: <LazyPage><ArticleMappings /></LazyPage> },
      { path: 'sellers', element: <LazyPage><Sellers /></LazyPage> },
      { path: 'users', element: <LazyPage><UsersAccess /></LazyPage> },
      // Orders скрыт фича-флагом: страница показывает мок-данные без бэкенда
      ...(FEATURES.orders ? [{ path: 'orders', element: <LazyPage><Orders /></LazyPage> }] : []),
      { path: 'wildberries', element: <LazyPage><WildberriesCatalog /></LazyPage> },
      { path: 'wildberries-old', element: <LazyPage><WildberriesLegacy /></LazyPage> },
      { path: 'wildberries-v2', element: <WildberriesV2Alias /> },
      { path: 'products/wb-cards', element: <LazyPage><WbCardsEditor /></LazyPage> },
      { path: 'wildberries/linking', element: <LazyPage><ProductLinking /></LazyPage> },
      // RestoreCharacteristics is embedded within the legacy Wildberries page (requires props)
      { path: 'ozon', element: <LazyPage><OzonCatalog /></LazyPage> },
      { path: 'ozon-old', element: <LazyPage><OzonLegacy /></LazyPage> },
      { path: 'ozon-v2', element: <OzonV2Alias /> },
      { path: 'yandex-market', element: <LazyPage><YandexMarketCatalog /></LazyPage> },
      { path: 'marketplace/publish/:planId', element: <LazyPage><MarketplacePublishPreview /></LazyPage> },
      { path: 'marketplace/card-changes', element: <LazyPage><MarketplaceCardChanges /></LazyPage> },
      { path: 'onec-products', element: <LazyPage><OneCProducts /></LazyPage> },
      { path: 'site-cards', element: <LazyPage><SiteProducts /></LazyPage> },
      { path: 'site-products', element: <SiteProductsAlias /> },
      { path: 'moysklad', element: <LazyPage><MoySklad /></LazyPage> },
      { path: 'notifications', element: <LazyPage><Notifications /></LazyPage> },
      { path: 'audit-log', element: <LazyPage><AuditLog /></LazyPage> },
      // ── ЛК Дизайнера ──
      { path: 'designer/create', element: <LazyPage><DesignerCreateLayouts /></LazyPage> },
      { path: 'designer/stats', element: <LazyPage><DesignerStats /></LazyPage> },
      { path: 'designer/payouts', element: <LazyPage><DesignerPayouts /></LazyPage> },
      { path: 'designer/tariffs', element: <LazyPage><DesignerTariffs /></LazyPage> },
      // Выплаты дизайнерам (продакт)
      { path: 'designer-payouts', element: <DesignerPayoutsAlias /> },
      // Заглушки для будущих разделов (левое меню)
      { path: 'designers', element: <LazyPage><DesignersTabs /></LazyPage> },
      { path: 'designs-analytics', element: <DesignsAnalyticsAlias /> },
      // ── SEO: две страницы по каноническим адресам, всё прочее — редиректы ──
      ...seoRoutes,
      { path: 'seasonal/products', element: <SeasonalProductsAlias /> },
      { path: 'seasonal/events', element: <SeasonalEventsAlias /> },
      { path: 'pricing/unit-economics', element: <LazyPage><UnitEconomics /></LazyPage> },
      { path: 'pricing/price-index', element: <LazyPage><PriceIndex /></LazyPage> },
      { path: 'pricing/wb', element: <LazyPage><PricingWb /></LazyPage> },
      { path: 'pricing/ozon', element: <LazyPage><PricingOzon /></LazyPage> },
      { path: 'stocks/stop-list', element: <LazyPage><StockStopList /></LazyPage> },
      { path: 'stocks/autoupdate', element: <LazyPage><StockAutoupdate /></LazyPage> },
      { path: 'stocks/runs', element: <LazyPage><StockRuns /></LazyPage> },
      { path: 'stocks/warehouses', element: <LazyPage><StockWarehouses /></LazyPage> },
      { path: 'stocks/settings', element: <LazyPage><StockSettings /></LazyPage> },
      { path: 'stocks/banned-brands', element: <LazyPage><StockBannedBrands /></LazyPage> },
      { path: 'stocks/unliquid', element: <LazyPage><StockUnliquid /></LazyPage> },
      { path: 'stocks/manual', element: <LazyPage><StockManual /></LazyPage> },
      { path: 'supplies/wb', element: <LazyPage><SuppliesPage marketplace="wb" /></LazyPage> },
      { path: 'supplies/ozon', element: <LazyPage><SuppliesPage marketplace="ozon" /></LazyPage> },
      { path: 'analytics/heroes', element: <LazyPage><TableRowActionsProvider><AnalyticsHeroes /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/cost', element: <LazyPage><TableRowActionsProvider><AnalyticsCost /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/sales', element: <LazyPage><TableRowActionsProvider><AnalyticsSales /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/data-quality', element: <LazyPage><TableRowActionsProvider><AnalyticsDataQuality /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/metrics', element: <LazyPage><TableRowActionsProvider><AnalyticsMetricsRegistry /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/explorer', element: <LazyPage><TableRowActionsProvider><AnalyticsMetricsExplorer /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/seasonal', element: <LazyPage><TableRowActionsProvider><AnalyticsSeasonal /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/designs', element: <LazyPage><TableRowActionsProvider><AnalyticsDesigns /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/abc', element: <LazyPage><TableRowActionsProvider><AnalyticsAbc /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/cabinets', element: <LazyPage><TableRowActionsProvider><AnalyticsCabinets /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/stocks', element: <LazyPage><TableRowActionsProvider><AnalyticsStocks /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/pnl', element: <LazyPage><TableRowActionsProvider><AnalyticsPnl /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/expenses', element: <LazyPage><TableRowActionsProvider><AnalyticsExpenses /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/reconciliation', element: <LazyPage><TableRowActionsProvider><AnalyticsReconciliation /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics', element: <LazyPage><TableRowActionsProvider><AnalyticsSummary /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/alerts', element: <LazyPage><TableRowActionsProvider><AnalyticsAlerts /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/rnp', element: <LazyPage><TableRowActionsProvider><AnalyticsRnp /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/plan', element: <LazyPage><TableRowActionsProvider><AnalyticsPlan /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/funnel', element: <LazyPage><TableRowActionsProvider><AnalyticsFunnel /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/ads', element: <LazyPage><TableRowActionsProvider><AnalyticsAds /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/prices', element: <LazyPage><TableRowActionsProvider><AnalyticsPrices /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/feedbacks', element: <LazyPage><TableRowActionsProvider><AnalyticsFeedbacks /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/geo', element: <LazyPage><TableRowActionsProvider><AnalyticsGeo /></TableRowActionsProvider></LazyPage> },
      { path: 'analytics/returns', element: <LazyPage><TableRowActionsProvider><AnalyticsReturns /></TableRowActionsProvider></LazyPage> },
      { path: 'integrations/wildberries', element: <LazyPage><StubPage title="Интеграция Вайлдберриз" /></LazyPage> },
      { path: 'integrations/ozon', element: <LazyPage><StubPage title="Интеграция ОЗОН" /></LazyPage> },
      { path: 'integrations/1c', element: <OneCIntegrationAlias /> },
      { path: 'integrations/site', element: <SiteIntegrationAlias /> },
      { path: 'integrations/licensium', element: <LicensiumIntegrationAlias /> },
      { path: 'integrations/bitrix24', element: <LazyPage><StubPage title="Интеграция Битрикс24" /></LazyPage> },
      { path: 'integrations/ai', element: <LazyPage><StubPage title="Интеграция ИИ" /></LazyPage> },
      { path: 'integrations', element: <LazyPage><Integrations /></LazyPage> },
      { path: 'integrations/services', element: <ExternalServicesAlias /> },
      { path: 'settings', element: <LazyPage><Settings /></LazyPage> },
      { path: 'settings/agent', element: <LazyPage><AgentAdmin /></LazyPage> },
      { path: 'settings/agents', element: <LazyPage><AgentConnections /></LazyPage> },
      { path: 'settings/business-access', element: <LazyPage><BusinessAccess /></LazyPage> },
      { path: 'settings/business-agents', element: <LazyPage><BusinessConnections /></LazyPage> },
      { path: 'settings/bitrix-bot', element: <LazyPage><BitrixBotAdmin /></LazyPage> },
      { path: 'settings/legacy-photogen', element: <LazyPage><LegacyContentFunnels /></LazyPage> },
      { path: 'settings/storage', element: <LazyPage><StorageSettings /></LazyPage> },
      { path: 'settings/ftp', element: <FtpSettingsAlias /> },
      { path: 'settings/prod-sync', element: <LazyPage><ProdSync /></LazyPage> },
      { path: 'settings/makets-storage', element: <MaketsStorageAlias /> },
      { path: 'settings/ai', element: <LazyPage><AiSettings /></LazyPage> },
      { path: 'settings/mail', element: <LazyPage><MailSettings /></LazyPage> },
      { path: 'settings/notifications', element: <LazyPage><NotificationPreferences /></LazyPage> },
      { path: 'settings/marketplace-link', element: <MarketplaceLinkAlias /> },
      { path: 'settings/backlog', element: <LazyPage><Backlog /></LazyPage> },
      { path: 'backlog/mine', element: <LazyPage><MyReports /></LazyPage> },
      { path: 'settings/access', element: <AccessManagementAlias /> },
      { path: 'admin/console', element: <LazyPage><AdminConsole /></LazyPage> },
      { path: 'admin/logs', element: <LazyPage><Logs /></LazyPage> },
      { path: 'design-system', element: <LazyPage><DesignSystem /></LazyPage> },
      { path: '*', element: <Navigate to="/" replace /> },
    ]),
  },
])
